import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  BudgetView, InventorySummary, LossKind, MarkSold, MyDeclaration, MyLosses, MyReturns, Piece,
  ReturnRequestCreate,
} from '@miraki/contracts';
import { api, ApiError } from '../../api/client';
import { useSession } from '../../auth/session';
import { Alert, Button, Chip, Empty, Field, Modal, Money, Note, PageHeader, Panel, Spinner, Table, Tiles } from '../../ui';
import {
  monthsHeld, notYet, partition, saleDateBounds, search, wholesaleByCurrency, type NotYet,
} from './consignment-read';
import { refusalLines } from './returns-view';
import { stockRefusalLines } from '../stock-desk';
import { isOutOfView, prefersReducedMotion, revealBehavior } from './reveal';
import { invalidateAll } from '../../api/write-keys';

/**
 * R-20 My Inventory — CN-03…CN-07, CN-13 (selection only), SL-01…SL-05, and the budget header.
 *
 * Built against the FROZEN contract (`zPiece`, `zInventorySummary`, `zMarkSold`, `zBudgetView`,
 * and `zReturnRequestCreate` for CN-13's request, which R-25 then shows),
 * not against the service, which still throws `WS-1a`. Where the API says NOT_YET the screen says
 * what it is waiting for; any other failure is an error (`notYet()` decides, and nothing else).
 *
 * Mark as Sold is SL-01's sequence and nothing more: tap, confirm, done (D68). The confirmation
 * shows the piece, its Job Number and the wholesale amount that becomes payable (SL-02). There is
 * no selling-price or customer field: D67 proposed against capturing them, and they are not added.
 *
 * Actions follow the signed-in user's permissions, which the server also enforces:
 * `shop.report_sale` for Mark as Sold, `shop.return_request` for the return selection,
 * `shop.loss_report` for reporting a piece lost, damaged or stolen (CN-10).
 *
 * A loss report takes the piece out of the held list at once — it is no longer stock to sell — and
 * the "Lost, damaged or stolen" panel then shows what Miraki decided, including a charge, which is
 * the one outcome the retailer pays for (D39/D81 decide which to expect; until then Miraki chooses).
 */

type Pending<T> = { state: 'READY'; value: T } | { state: 'NOT_YET'; waitingFor: string };
interface DashboardSlice { today: string; tiles: Array<{ id: string; data: unknown }> }
interface Inventory { summary: InventorySummary; pieces: Piece[]; declaration: MyDeclaration | null }

/**
 * SL-04a — the retailer's own deadline, on the screen where a sale is reported.
 *
 * **It never refuses anything and it is never a `stop`.** The tone changes and the presence does
 * not: a notice while the month is open or closing, a lapse once the deadline has passed, and a
 * quiet confirmation once something has been reported. Nothing here disables Mark as Sold — SL-03a:
 * *"what waits is the release, not the report."*
 *
 * Both halves of the copy are shown together, deliberately. The deadline alone teaches a retailer
 * to log in at month end, by which time SL-04's seven-day bound refuses the true dates for most of
 * the month — so the earliest date still accepted is named beside it. And neither sentence says
 * that declaring frees budget: SL-03a makes the release wait for Admin.
 *
 * Shown only to someone who may actually report a sale (RL-05 — a prompt you cannot act on is not
 * shown), and null on a portal with no month to close.
 */
function DeclarationNotice({ d }: { d: MyDeclaration }) {
  const { t } = useTranslation();
  if (d.declared) {
    return (
      <Alert tone="them">
        {t('declarations.myDeclared', { deadline: d.deadline })}{' '}
        <span className="mk-hint">{t('declarations.myBackstop', { earliest: d.earliestSaleDate })}</span>
      </Alert>
    );
  }
  return (
    <Alert tone={d.state === 'LATE' ? 'lapse' : 'us'}>
      {d.state === 'LATE'
        ? t('declarations.myLate', { deadline: d.deadline })
        : t('declarations.myDue', { deadline: d.deadline })}{' '}
      <span className="mk-hint">{t('declarations.myBackstop', { earliest: d.earliestSaleDate })}</span>
    </Alert>
  );
}

/** Reads that may come back NOT_YET. The query resolves to either the data or the reason. */
async function readOrNotYet<T>(path: string): Promise<{ data: T } | { notYet: NotYet }> {
  try {
    return { data: await api.get<T>(path) };
  } catch (err) {
    const n = notYet(err);
    if (n) return { notYet: n };
    throw err;
  }
}

function WaitingFor({ what }: { what: NotYet }) {
  const { t } = useTranslation();
  return <Note>{t('myInventory.notYet')}{what.waitingFor ? <span className="mk-dim"> ({what.waitingFor})</span> : null}</Note>;
}

function Amounts({ list }: { list: ReadonlyArray<{ currency: string; amount: string }> }) {
  return <>{list.map((a) => <div key={a.currency}><Money amount={a.amount} currency={a.currency} /></div>)}</>;
}

/**
 * A panel that has just opened, brought to the reader — and the keyboard brought with it.
 *
 * Pressing a button must change what is on the screen. Both draft panels are mounted by a
 * state flag, so "nothing happened" and "it opened somewhere you cannot see" look identical
 * from the viewport, and the second is what was happening (`./reveal.ts`). The focus moves too:
 * a sighted reader and a screen-reader user should end up in the same place, and that place is
 * the panel, not the button that is now off screen.
 *
 * Returns the ref to put on the panel's outermost element.
 */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const still = prefersReducedMotion();
    if (isOutOfView(el.getBoundingClientRect(), window.innerHeight)) {
      el.scrollIntoView({ behavior: revealBehavior(still), block: 'start' });
    }
    // `preventScroll` so focusing does not fight the scroll just asked for, and does not jump
    // past it when the panel was already in view.
    el.focus({ preventScroll: true });
  }, []);
  return ref;
}

export function MyInventory() {
  const { t } = useTranslation();
  const { me } = useSession();
  const qc = useQueryClient();
  const permissions = me?.kind === 'retailer' ? me.permissions : [];
  const mayReportSale = permissions.includes('shop.report_sale');
  const mayRequestReturn = permissions.includes('shop.return_request');
  const mayReportLoss = permissions.includes('shop.loss_report');

  const inventory = useQuery({ queryKey: ['shop', 'inventory'], queryFn: () => readOrNotYet<Inventory>('/shop/inventory') });
  // The budget, and the portal's "today", from R-01's route: it already calls the one resolver
  // and composes to the user's permissions, so a user who may not see the budget gets none here.
  const dashboard = useQuery({ queryKey: ['shop', 'dashboard'], queryFn: () => api.get<DashboardSlice>('/shop/dashboard') });
  const losses = useQuery({ queryKey: ['shop', 'losses'], queryFn: () => api.get<MyLosses>('/shop/losses') });
  // CN-09 — which approved returns are Miraki's recalls, so the chip says who asked. Only readable
  // with R-25's permission; without it the chip says "approved", which is still true.
  const returns = useQuery({ queryKey: ['shop', 'returns'], queryFn: () => api.get<MyReturns>('/shop/returns'), enabled: mayRequestReturn });
  const recalled = useMemo(() => new Set((returns.data?.requests ?? [])
    .filter((r) => r.origin === 'MIRAKI').flatMap((r) => r.lines.filter((l) => l.state === 'APPROVED').map((l) => l.pieceId))), [returns.data]);

  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState<Piece[] | null>(null);
  const [returning, setReturning] = useState(false);
  const [reporting, setReporting] = useState<Piece | null>(null);

  const loaded = inventory.data && 'data' in inventory.data ? inventory.data.data : null;
  const groups = useMemo(() => partition(loaded?.pieces ?? []), [loaded]);
  const held = search(groups.HELD, q);
  const selected = groups.HELD.filter((p) => picked.has(p.id));
  const onRequest = selected.filter((p) => p.returnRequest !== null);
  const focus = groups.HELD.find((p) => p.id === open) ?? null;
  const budget = dashboard.data?.tiles.find((x) => x.id === 'budget')?.data as Pending<BudgetView> | undefined;
  const today = dashboard.data?.today ?? null;

  if (inventory.isLoading) return <Spinner />;
  if (inventory.error) return <Alert tone="stop">{inventory.error.message}</Alert>;
  const result = inventory.data!;

  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  /** BG-02 — what is holding the budget, so a retailer can answer "why can I not order". */
  const bg = budget?.state === 'READY' ? budget.value : null;
  const budgetTiles = bg === null ? [] : [
    { k: t('myInventory.available'), v: <Money amount={bg.available} currency={bg.currency} />, s: t('myInventory.ofAssigned', { amount: bg.assigned }) },
    { k: t('myInventory.inUse.openOrders'), v: <Money amount={bg.inUse.openOrders} currency={bg.currency} /> },
    { k: t('myInventory.inUse.inTransit'), v: <Money amount={bg.inUse.inTransit} currency={bg.currency} /> },
    { k: t('myInventory.inUse.held'), v: <Money amount={bg.inUse.held} currency={bg.currency} /> },
    // BG-02 (d) — only where the budget is released on payment rather than on sale.
    ...(bg.releaseOn === 'PAYMENT' ? [{ k: t('myInventory.inUse.soldUnpaid'), v: <Money amount={bg.inUse.soldUnpaid} currency={bg.currency} /> }] : []),
  ];

  return (
    <>
      <PageHeader title={t('myInventory.title')} />

      {budget?.state === 'NOT_YET' ? <WaitingFor what={{ waitingFor: budget.waitingFor }} /> : null}

      {/* SL-04a — above the figures, because it is the one thing on this screen with a date on it. */}
      {mayReportSale && loaded?.declaration ? <DeclarationNotice d={loaded.declaration} /> : null}

      {'notYet' in result ? (
        <>
          {budgetTiles.length ? <Tiles items={budgetTiles} /> : null}
          <WaitingFor what={result.notYet} />
        </>
      ) : (
        <>
          {/* ONE tile grid, not two. The budget figures and the inventory figures were sibling
              `Tiles` grids, so `auto-fit` laid each out on its own and the page read as a row of
              four and then a row of three — a layout accident rather than a grouping. They are
              all figures about what this account holds, and one grid gives them one rhythm. */}
          <Tiles items={[
            ...budgetTiles,
            { k: t('myInventory.wholesale'), v: <Money amount={result.data.summary.wholesaleValue} currency={result.data.summary.currency} /> },
            { k: t('myInventory.rp'), v: <Money amount={result.data.summary.rpValue} currency={result.data.summary.currency} /> },
            { k: t('myInventory.onHand'), v: result.data.summary.piecesOnHand },
          ]} />
          {result.data.summary.byArticle.length ? (
            <p className="mk-hint">
              {result.data.summary.byArticle.map((a) => <span key={a.article}><Chip tone="them">{a.article} · {a.pieces}</Chip>{' '}</span>)}
            </p>
          ) : null}

          {/* CN-13, CN-10 — the two draft panels open ABOVE the table, not under everything on
              the page. Mounted at the foot, "Request return of N" changed nothing a retailer
              could see: the panel was in the accessibility tree a full table's height below the
              fold, so the button read as broken and was pressed again. They also take the
              reader with them when they open (`useReveal`), because on a long table the foot of
              the list is just as far away as the foot of the page. */}
          {reporting ? <LossDraft piece={reporting} onClose={() => setReporting(null)} onSent={() => { setReporting(null); setOpen(null); setPicked(new Set()); }} /> : null}
          {returning && selected.length ? <ReturnDraft pieces={selected} onClose={() => setReturning(false)} onSent={() => { setReturning(false); setPicked(new Set()); }} /> : null}

          {/* The detail panel is only here when a piece is open, so the table has the page to
              itself until then — see `.mk-listdetail`. */}
          <div className={`mk-listdetail${focus ? ' on' : ''}`}>
            <Panel title={t('myInventory.held')}>
              <div className="mk-pbody">
                <Field label={t('myInventory.search')} hint={t('myInventory.searchHint')}>
                  <input type="search" value={q} onChange={(e) => setQ(e.target.value)} />
                </Field>
                {selected.length > 0 ? (
                  <p>
                    {mayReportSale ? <Button size="sm" onClick={() => setConfirming(selected)}>{t('myInventory.markSelected', { n: selected.length })}</Button> : null}{' '}
                    {mayRequestReturn ? (
                      // CN-13 — a piece already on a live request cannot go on a second one; say so
                      // here rather than let the server refuse it (it would, and does).
                      <Button
                        size="sm" tone="ghost" disabled={onRequest.length > 0}
                        {...(onRequest.length > 0 ? { title: t('myInventory.alreadyOnReturn', { jobs: onRequest.map((p) => p.jobNumber).join(', ') }) } : {})}
                        onClick={() => setReturning(true)}
                      >
                        {t('myInventory.returnSelected', { n: selected.length })}
                      </Button>
                    ) : null}{' '}
                    {mayRequestReturn && onRequest.length > 0
                      ? <span className="mk-hint">{t('myInventory.alreadyOnReturn', { jobs: onRequest.map((p) => p.jobNumber).join(', ') })} </span>
                      : null}
                    <Button size="sm" tone="ghost" onClick={() => setPicked(new Set())}>{t('myInventory.clear')}</Button>
                  </p>
                ) : null}
              </div>
              <Table
                rows={held}
                rowKey={(p) => p.id}
                columns={[
                  ...(mayReportSale || mayRequestReturn ? [{
                    header: '', width: '1%',
                    render: (p: Piece) => (
                      <input type="checkbox" aria-label={t('myInventory.pick', { job: p.jobNumber })} checked={picked.has(p.id)} onChange={() => toggle(p.id)} />
                    ),
                  }] : []),
                  // The Job Number is the piece's identity (invariant 3), so it gets a column of
                  // its own here as it has on the returns lists — not a hint line under the name.
                  { header: t('myInventory.jobNumber'), render: (p: Piece) => <code>{p.jobNumber}</code> },
                  {
                    header: t('myInventory.colPiece'),
                    render: (p: Piece) => (
                      <>
                        <button type="button" className="mk-rowopen" onClick={() => setOpen(p.id)}>{p.description}</button>
                        {p.returnRequest ? <div><Chip tone="run">{t(`myInventory.onReturn.${recalled.has(p.id) ? 'RECALLED' : p.returnRequest.state}`)}</Chip></div> : null}
                      </>
                    ),
                  },
                  { header: t('myInventory.colWholesale'), numeric: true, render: (p: Piece) => <Money amount={p.wholesaleValue} currency={p.currency} /> },
                  { header: t('myInventory.colRp'), numeric: true, render: (p: Piece) => <Money amount={p.rpValue} currency={p.currency} /> },
                  { header: t('myInventory.colDays'), numeric: true, render: (p: Piece) => p.daysHeld ?? '—' },
                  ...(mayReportSale ? [{
                    // No `width: '1%'` here as on the checkbox: under auto layout a width below
                    // min-content collapses the column to its longest WORD, which stacked
                    // "Mark as sold" three lines tall. The button needs its label's width.
                    header: '', center: true,
                    // SL-01, CN-14 — an APPROVED return is on its way back; it is not offered for sale.
                    render: (p: Piece) => (p.returnRequest?.state === 'APPROVED' ? null
                      : <Button size="sm" onClick={() => setConfirming([p])}>{t('myInventory.markSold')}</Button>),
                  }] : []),
                ]}
                empty={<Empty title={t(q ? 'myInventory.noMatch' : 'myInventory.emptyTitle')} body={q ? undefined : t('myInventory.emptyBody')} />}
              />
            </Panel>

            {focus ? (
              <Panel title={focus.description} pad actions={<Button size="sm" tone="ghost" onClick={() => setOpen(null)}>{t('myInventory.close')}</Button>}>
                <Table
                  rows={[
                    [t('myInventory.jobNumber'), focus.jobNumber],
                    [t('myInventory.goldColour'), focus.goldColour ?? '—'],
                    ...(focus.ringSize ? [[t('myInventory.ringSize'), focus.ringSize] as const] : []),
                    [t('myInventory.received'), focus.receivedAt?.slice(0, 10) ?? '—'],
                    [t('myInventory.daysHeld'), focus.daysHeld === null ? '—'
                      : t('myInventory.daysMonths', { days: focus.daysHeld, months: monthsHeld(focus.daysHeld) })],
                  ] as Array<readonly [string, string]>}
                  rowKey={([k]) => k}
                  columns={[{ header: '', render: ([k]) => <span className="mk-dim">{k}</span> }, { header: '', render: ([, v]) => v }]}
                />
                <p>
                  {mayReportSale && focus.returnRequest?.state !== 'APPROVED'
                    ? <Button size="sm" onClick={() => setConfirming([focus])}>{t('myInventory.markSold')}</Button> : null}{' '}
                  {mayReportLoss && focus.returnRequest === null
                    ? <Button size="sm" tone="ghost" onClick={() => setReporting(focus)}>{t('myInventory.reportLoss')}</Button> : null}
                </p>
                {mayReportLoss && focus.returnRequest !== null ? <p className="mk-hint">{t('myInventory.lossOnReturn')}</p> : null}
              </Panel>
            ) : null}
          </div>

          {groups.ON_THE_WAY.length ? (
            <Panel title={t('myInventory.onTheWay')}>
              <Table rows={groups.ON_THE_WAY} rowKey={(p) => p.id}
                columns={[
                  { header: t('myInventory.colPiece'), render: (p) => <>{p.description}<div className="mk-hint">{p.jobNumber}</div></> },
                  { header: t('myInventory.colState'), render: (p) => <Chip tone={p.status === 'DISCREPANCY' ? 'stop' : 'run'}>{t(`myInventory.state.${p.status}`)}</Chip> },
                  { header: t('myInventory.colWholesale'), numeric: true, render: (p) => <Money amount={p.wholesaleValue} currency={p.currency} /> },
                ]} />
              <div className="mk-pbody"><Note>{t('myInventory.onTheWayNote')}</Note></div>
            </Panel>
          ) : null}

          {losses.data && losses.data.reports.length ? (
            <Panel title={t('myInventory.reportsTitle')}>
              <Table rows={[...losses.data.reports]} rowKey={(r) => r.id}
                columns={[
                  { header: t('myInventory.colPiece'), render: (r) => <>{r.description}<div className="mk-hint">{r.jobNumber}</div></> },
                  { header: t('myInventory.colReported'), render: (r) => <>{t(`myInventory.lossKinds.${r.kind}`)} · {r.reportedAt.slice(0, 10)}</> },
                  {
                    header: t('myInventory.colOutcome'),
                    render: (r) => (
                      <>
                        <Chip tone={r.outcome === null ? 'us' : r.outcome === 'CHARGED' ? 'stop' : 'done'}>{t(`myInventory.lossOutcome.${r.outcome ?? 'OPEN'}`)}</Chip>
                        {r.outcome === 'CHARGED' ? <div><Money amount={r.wholesaleValue} currency={r.currency} /></div> : null}
                        {r.outcomeReason ? <div className="mk-hint">{r.outcomeReason}</div> : null}
                      </>
                    ),
                  },
                ]} />
              <div className="mk-pbody"><Note>{t('myInventory.reportedNote')}</Note></div>
            </Panel>
          ) : groups.REPORTED.length ? (
            <Panel title={t('myInventory.reported')}>
              <Table rows={groups.REPORTED} rowKey={(p) => p.id}
                columns={[
                  { header: t('myInventory.colPiece'), render: (p) => <>{p.description}<div className="mk-hint">{p.jobNumber}</div></> },
                  { header: t('myInventory.colState'), render: (p) => <Chip tone="stop">{t(`myInventory.state.${p.status}`)}</Chip> },
                ]} />
              <div className="mk-pbody"><Note>{t('myInventory.reportedNote')}</Note></div>
            </Panel>
          ) : null}

        </>
      )}

      {confirming ? (
        <ConfirmSale
          pieces={confirming}
          today={today}
          onClose={() => setConfirming(null)}
          onDone={async () => {
            setConfirming(null);
            setPicked(new Set());
            setOpen(null);
            await invalidateAll(qc, [
              ['shop', 'inventory'], ['shop', 'sold'], ['shop', 'dashboard'],
            ]);
          }}
        />
      ) : null}
    </>
  );
}

/** SL-01, SL-02, SL-04, SL-05 — confirm, with what becomes payable. No price, no customer (D67). */
function ConfirmSale({ pieces, today, onClose, onDone }: {
  pieces: Piece[]; today: string | null; onClose: () => void; onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [saleDate, setSaleDate] = useState(today ?? '');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [waiting, setWaiting] = useState<NotYet | null>(null);
  const bounds = today ? saleDateBounds(today) : null;

  async function confirm() {
    setBusy(true);
    setFailure(null);
    try {
      // Today is the default and is sent as no date at all, so the server applies its own "today".
      const body: MarkSold = { pieceIds: pieces.map((p) => p.id), ...(saleDate && saleDate !== today ? { saleDate } : {}) };
      await api.post('/shop/inventory/sold', body);
      await onDone();
    } catch (err) {
      const n = notYet(err);
      if (n) setWaiting(n);
      else setFailure(err instanceof ApiError ? err.message : t('myInventory.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('myInventory.confirmTitle', { n: pieces.length })}
      onClose={onClose}
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>{t('myInventory.cancel')}</Button>
          <Button disabled={busy || !!waiting} onClick={() => void confirm()}>{t('myInventory.confirm')}</Button>
        </>
      }
    >
      <Table rows={pieces} rowKey={(p) => p.id}
        columns={[
          { header: t('myInventory.jobNumber'), render: (p) => p.jobNumber },
          { header: t('myInventory.colPiece'), render: (p) => p.description },
          { header: t('myInventory.colWholesale'), numeric: true, render: (p) => <Money amount={p.wholesaleValue} currency={p.currency} /> },
        ]} />
      <p>{t('myInventory.becomesPayable')} <Amounts list={wholesaleByCurrency(pieces)} /></p>
      {bounds ? (
        <Field label={t('myInventory.saleDate')} hint={t('myInventory.saleDateHint')}>
          <input type="date" value={saleDate} min={bounds.min} max={bounds.max} onChange={(e) => setSaleDate(e.target.value)} />
        </Field>
      ) : null}
      <Note>{t('myInventory.cannotUndo')}</Note>
      {waiting ? <WaitingFor what={waiting} /> : null}
      {failure ? <Alert tone="stop">{failure}</Alert> : null}
    </Modal>
  );
}

/**
 * CN-13 — the selection, and the request. Sent to POST /shop/returns with the required remarks
 * (D79) and an optional note per piece. A piece stays HELD, and in budget use, for the whole life
 * of a request (CN-13); the server refuses a piece that is not held or already on a live request,
 * and reports every refusal, which is shown here by Job Number.
 */
function ReturnDraft({ pieces, onClose, onSent }: { pieces: Piece[]; onClose: () => void; onSent: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [remarks, setRemarks] = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[] | null>(null);
  const ready = remarks.trim().length >= 3 && remarks.trim().length <= 1000;
  const reveal = useReveal();

  const send = async () => {
    setBusy(true);
    setProblems(null);
    const kept = Object.fromEntries(Object.entries(notes).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, v.trim()]));
    const body: ReturnRequestCreate = {
      pieceIds: pieces.map((p) => p.id), remarks: remarks.trim(),
      ...(Object.keys(kept).length ? { notes: kept } : {}),
    };
    try {
      await api.post('/shop/returns', body);
      await invalidateAll(qc, [['shop']]);
      onSent();
      void navigate('/shop/returns');
    } catch (e) {
      const lines = refusalLines(e, (id) => pieces.find((p) => p.id === id)?.jobNumber);
      setProblems(lines ? lines.map((l) => t(l.key, l.values)) : [e instanceof Error ? e.message : t('myInventory.returnFailed')]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={reveal} tabIndex={-1}>
      <Panel title={t('myInventory.returnTitle', { n: pieces.length })} pad actions={<Button size="sm" tone="ghost" onClick={onClose}>{t('myInventory.close')}</Button>}>
        <Table rows={pieces} rowKey={(p) => p.id}
          columns={[
            { header: t('myInventory.jobNumber'), render: (p) => p.jobNumber },
            { header: t('myInventory.colPiece'), render: (p) => p.description },
            { header: t('myInventory.colWholesale'), numeric: true, render: (p) => <Money amount={p.wholesaleValue} currency={p.currency} /> },
            {
              header: t('myInventory.returnNote'),
              render: (p) => (
                <input
                  className="mk-fld" value={notes[p.id] ?? ''} maxLength={1000}
                  aria-label={t('myInventory.returnNoteFor', { job: p.jobNumber })}
                  onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
                />
              ),
            },
          ]} />
        <p>{t('myInventory.returnValue')} <Amounts list={wholesaleByCurrency(pieces)} /></p>
        <Field label={t('myInventory.remarks')} hint={t('myInventory.remarksHint')}>
          <textarea value={remarks} maxLength={1000} onChange={(e) => setRemarks(e.target.value)} />
        </Field>
        <Note>{t('myInventory.returnStaysHeld')}</Note>
        {problems ? <Alert tone="stop">{problems.map((m) => <div key={m}>{m}</div>)}</Alert> : null}
        <p>
          <Button size="sm" disabled={!ready || busy} onClick={() => void send()}>{t('myInventory.submitReturn')}</Button>
          {!ready ? <span className="mk-hint"> {t('myInventory.remarksRequired')}</span> : null}
        </p>
      </Panel>
    </div>
  );
}

/**
 * CN-10 — the retailer reports a piece they hold lost, damaged or stolen. It leaves the held list at
 * once (HELD → LOST or DAMAGED, with its movement), and the panel says plainly what may follow: the
 * outcome is Miraki's, and one of the three is a charge at the wholesale value.
 */
function LossDraft({ piece, onClose, onSent }: { piece: Piece; onClose: () => void; onSent: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<LossKind>('LOST');
  const [account, setAccount] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const ready = account.trim().length >= 10;
  const reveal = useReveal();

  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.post('/shop/losses', { pieceId: piece.id, kind, account: account.trim() });
      await invalidateAll(qc, [['shop']]);
      onSent();
    } catch (e) {
      const lines = stockRefusalLines(e, (id) => (id === piece.id ? piece.jobNumber : undefined));
      setProblem(lines ? lines.map((l) => t(l.key, l.values)).join(' ') : e instanceof ApiError ? e.message : t('myInventory.lossFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={reveal} tabIndex={-1}>
      <Panel title={t('myInventory.lossTitle', { job: piece.jobNumber })} pad actions={<Button size="sm" tone="ghost" onClick={onClose}>{t('myInventory.close')}</Button>}>
        <p>{piece.description}</p>
        <Field label={t('myInventory.lossKind')}>
          <select value={kind} onChange={(e) => setKind(e.target.value as LossKind)}>
            {(['LOST', 'DAMAGED', 'STOLEN'] as const).map((k) => <option key={k} value={k}>{t(`myInventory.lossKinds.${k}`)}</option>)}
          </select>
        </Field>
        <Field label={t('myInventory.lossAccount')} hint={t('myInventory.lossAccountHint')}>
          <textarea value={account} maxLength={1000} onChange={(e) => setAccount(e.target.value)} />
        </Field>
        <Note>{t('myInventory.lossGoes')} <Money amount={piece.wholesaleValue} currency={piece.currency} /></Note>
        {problem ? <Alert tone="stop">{problem}</Alert> : null}
        <p><Button size="sm" disabled={!ready || busy} onClick={() => void send()}>{t('myInventory.sendLoss')}</Button></p>
      </Panel>
    </div>
  );
}
